import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Boxes, BookOpen, KeyRound, Webhook, BarChart3, PlayCircle, Activity, Component, LogOut, Sun, Moon } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { useDarkMode, useChromeT, getLocale, setLocale } from '@/lib/ui';

const nav = [
  { name: 'Applications', href: '/apps', icon: KeyRound },
  { name: 'API Reference', href: '/reference', icon: BookOpen },
  { name: 'Guides', href: '/guides', icon: Boxes },
  { name: 'Webhooks', href: '/webhooks', icon: Webhook },
  { name: 'Usage', href: '/usage', icon: BarChart3 },
  { name: 'Playground', href: '/playground', icon: PlayCircle },
  { name: 'Status', href: '/status', icon: Activity },
  { name: 'Components', href: '/components', icon: Component },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, signOut } = useAuthStore();
  const [dark, toggleDark] = useDarkMode();
  const t = useChromeT();
  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      <aside className="w-60 bg-gray-900 text-gray-200 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <p className="text-lg font-semibold text-white">Developer Portal</p>
            <p className="text-xs text-gray-400">Auth-as-a-Service</p>
          </div>
          <div className="flex items-center gap-1">
            <select value={getLocale()} onChange={(e) => setLocale(e.target.value)} className="text-xs bg-gray-800 text-gray-200 rounded px-1 py-0.5" title="Language">
              <option value="en">EN</option><option value="es">ES</option><option value="fr">FR</option>
            </select>
            <button onClick={toggleDark} className="text-gray-400 hover:text-white" title="Toggle theme">{dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            const active = location.pathname.startsWith(n.href);
            return (
              <Link key={n.href} to={n.href} className={`flex items-center px-3 py-2 rounded-md text-sm ${active ? 'bg-gray-800 text-white' : 'hover:bg-gray-800'}`}>
                <Icon className="w-4 h-4 mr-3" /> {t(n.name)}
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-gray-800">
          <p className="text-xs text-gray-400 px-3 mb-2 truncate">{user?.email}</p>
          <button onClick={() => signOut()} className="flex items-center px-3 py-2 rounded-md text-sm hover:bg-gray-800 w-full">
            <LogOut className="w-4 h-4 mr-3" /> {t('Sign out')}
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto dark:text-gray-100">{children}</main>
    </div>
  );
}
